import { Trash2 } from 'lucide-react';

import { STRUCTURAL_EDIT_LOCK_REASON } from './questionEditRestrictions';
import styles from './MultipleChoiceEditor.module.css';

/**
 * The answer-options editor for both multiple choice and select-all.
 *
 * Shared rather than forked for the same reason the student control is: the two
 * differ only in the input type and in whether marking replaces or adds. The
 * caller owns that semantic — this component just reports which option was
 * marked.
 */
interface MultipleChoiceEditorProps {
  correctAnswerIndex?: number;
  /** SELECT_ALL only; ignored unless multiSelect is set. */
  correctAnswerIndices?: number[];
  disabled?: boolean;
  groupName: string;
  /** Renders checkboxes so several options can be marked correct. */
  multiSelect?: boolean;
  onAddOption: () => void;
  onChangeCorrectAnswer: (index: number) => void;
  onChangeOption: (index: number, value: string) => void;
  onRemoveOption: (index: number) => void;
  options: string[];
  structuralEditsRestricted?: boolean;
}

export function MultipleChoiceEditor({
  correctAnswerIndex,
  correctAnswerIndices,
  disabled = false,
  groupName,
  multiSelect = false,
  onAddOption,
  onChangeCorrectAnswer,
  onChangeOption,
  onRemoveOption,
  options,
  structuralEditsRestricted = false,
}: MultipleChoiceEditorProps) {
  const structuralDisabled = disabled || structuralEditsRestricted;

  return (
    <fieldset className={styles.root}>
      <legend className={styles.legend}>Answer Options</legend>

      <ol className={styles.list}>
        {options.map((option, index) => (
          <li className={styles.optionRow} key={`option-${index + 1}`}>
            <input
              aria-label={`Mark option ${index + 1} correct`}
              checked={
                multiSelect
                  ? (correctAnswerIndices ?? []).includes(index)
                  : correctAnswerIndex === index
              }
              className={styles.correctInput}
              disabled={disabled}
              // Checkboxes must not share a name — a shared name is what makes
              // a radio group single-select.
              name={multiSelect ? `${groupName}-${index}` : groupName}
              onChange={() => {
                onChangeCorrectAnswer(index);
              }}
              type={multiSelect ? 'checkbox' : 'radio'}
            />
            <input
              aria-label={`Option ${index + 1}`}
              className={styles.optionInput}
              disabled={disabled}
              onChange={(event) => {
                onChangeOption(index, event.target.value);
              }}
              placeholder={`Option ${index + 1}`}
              type="text"
              value={option}
            />
            {options.length > 2 ? (
              <button
                aria-label={`Delete option ${index + 1}`}
                className={styles.optionDelete}
                disabled={structuralDisabled}
                onClick={() => {
                  onRemoveOption(index);
                }}
                title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
                type="button"
              >
                <Trash2 aria-hidden="true" size={16} strokeWidth={2} />
              </button>
            ) : null}
          </li>
        ))}
      </ol>

      <button
        className={styles.addButton}
        disabled={structuralDisabled}
        onClick={onAddOption}
        title={structuralEditsRestricted ? STRUCTURAL_EDIT_LOCK_REASON : undefined}
        type="button"
      >
        Add Option
      </button>
    </fieldset>
  );
}
